import React from "react";
import { Plan, PLAN_LABELS } from "../config/plans.config";
import { useAuth } from "../hooks/useAuth";

// ─────────────────────────────────────────────────────────────────────────────
// Small inline lock badge — drop next to a button label or tab title
// Usage: <PlanBadge requiredPlan="pro" />
// ─────────────────────────────────────────────────────────────────────────────
export const PlanBadge: React.FC<{ requiredPlan: Plan }> = ({
  requiredPlan,
}) => (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FEF9C3] border border-[#FDE68A] text-[#92400E] text-[9px] font-bold ml-1.5 select-none">
    🔒 {PLAN_LABELS[requiredPlan]}+
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper that dims its children and shows a tooltip on hover.
// Clicking fires onUpgradeClick so the parent can open the modal.
// ─────────────────────────────────────────────────────────────────────────────
export const LockedOverlay: React.FC<{
  feature: string;
  requiredPlan: Plan;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  tooltipBelow?: boolean;
}> = ({ feature, requiredPlan, onClick, children, className = "", tooltipBelow = false }) => (
  <div
    className={`relative group cursor-not-allowed ${className}`}
    onClick={onClick}
    title={`${feature} requires ${PLAN_LABELS[requiredPlan]}+`}
  >
    <div className="pointer-events-none opacity-50 select-none">{children}</div>
    {/* Hover tooltip */}
    <div
      className={`absolute left-1/2 -translate-x-1/2 z-50
                    opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none
                    ${tooltipBelow ? "top-full mt-1.5" : "bottom-full mb-1.5"}`}
    >
      {/* Arrow — above the box when tooltip is below, below the box when tooltip is above */}
      {tooltipBelow && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2
                        border-4 border-transparent border-b-[#1F2937]"
        />
      )}
      <div
        className="bg-[#1F2937] text-white text-[11px] font-medium rounded-lg
                      px-2.5 py-1.5 shadow-lg whitespace-nowrap"
      >
        🔒 {feature} ·{" "}
        <span className="text-[#FCD34D]">{PLAN_LABELS[requiredPlan]}+</span>
      </div>
      {!tooltipBelow && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2
                        border-4 border-transparent border-t-[#1F2937]"
        />
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Full-screen upgrade modal — shown when a user actively tries a gated feature
// ─────────────────────────────────────────────────────────────────────────────
interface UpgradeModalProps {
  feature: string;
  requiredPlan: Plan;
  description?: string;
  onClose: () => void;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  feature,
  requiredPlan,
  description,
  onClose,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-14 w-14 rounded-full bg-[#FEF9C3] flex items-center justify-center mx-auto mb-4 text-2xl">
          🔒
        </div>

        <h2 className="text-lg font-bold text-[#111827] mb-2">{feature}</h2>

        <p className="text-sm text-[#6B7280] mb-1">
          {description ?? "This feature is available on the"}
        </p>
        <p className="text-sm font-semibold text-[#111827] mb-5">
          {PLAN_LABELS[requiredPlan]} plan and above.
        </p>

        {requiredPlan === "enterprise" ? (
          <>
            <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl px-4 py-3 mb-5">
              <p className="text-xs text-[#5B21B6]">
                Enterprise includes SSO, unlimited seats, and custom terms.
                Talk to us for a quote.
              </p>
            </div>
            <a
              href="mailto:hello@sentrilog.com?subject=Enterprise%20plan"
              className="block w-full py-2.5 bg-[#111827] text-white rounded-lg text-sm font-semibold hover:bg-[#1F2937] mb-3"
            >
              Contact sales
            </a>
          </>
        ) : !isAdmin ? (
          <div className="bg-[#F3F4F6] rounded-xl px-4 py-3 mb-5">
            <p className="text-xs text-[#4B5563]">
              Only a workspace <strong>admin</strong> can upgrade.
              Ask your admin to upgrade this workspace.
            </p>
          </div>
        ) : (
          <a
            href="/pricing"
            onClick={onClose}
            className="block w-full py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] transition-colors mb-3"
          >
            View Plans &amp; Upgrade →
          </a>
        )}

        <button
          onClick={onClose}
          className="text-xs text-[#6B7280] hover:text-[#374151] transition-colors"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Usage progress bar — show inside settings or profile to display consumption
// Usage: <UsageBar label="AI Scans" used={3} limit={5} requiredPlan="pro" />
// ─────────────────────────────────────────────────────────────────────────────
export const UsageBar: React.FC<{
  label: string;
  used: number;
  limit: number; // -1 = unlimited
  requiredPlan: Plan;
}> = ({ label, used, limit }) => {
  if (limit === -1) {
    return (
      <div className="text-xs text-[#6B7280] flex justify-between">
        <span>{label}</span>
        <span className="font-medium text-[#059669]">Unlimited</span>
      </div>
    );
  }

  const pct = Math.min((used / limit) * 100, 100);
  const isNear = pct >= 80;
  const isFull = pct >= 100;

  return (
    <div className="text-xs space-y-1">
      <div className="flex justify-between text-[#6B7280]">
        <span>{label}</span>
        <span className={isFull ? "text-[#DC2626] font-semibold" : ""}>
          {used} / {limit}
          {isFull && (
            <a
              href="/pricing"
              className="ml-1.5 text-[#2563EB] hover:underline font-semibold"
            >
              Upgrade
            </a>
          )}
        </span>
      </div>
      <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isFull ? "bg-[#DC2626]" : isNear ? "bg-[#F59E0B]" : "bg-[#2563EB]"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {isNear && !isFull && (
        <p className="text-[10px] text-[#F59E0B]">
          Running low —{" "}
          <a href="/pricing" className="underline">
            view plans
          </a>{" "}
          for more.
        </p>
      )}
    </div>
  );
};
